/**
 * /field/ws — a well-servicing hand's tour sheets (GET
 * /well-servicing/field/my-tours, fieldiq-api#1282, WS-2).
 *
 * THE SELECTION IS THE API'S: the open jobs the caller is on (the rig's crew
 * or a sheet's crew), each with its sheet for the tenant's today, and the
 * caller's sheets of the last 14 days. This screen only lays them out. The
 * field surface is SNAKE_CASE (job_number, today_tour_id, days_ago).
 *
 * The frame is Pressure Testing's field frame (PtFieldPage), imported, so the
 * verticals' field screens stay one shape.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, ClipboardList, MapPin } from 'lucide-react';
import { useWsFieldMyTours } from '../../../hooks/useWsJobs';
import { wsDay, wsHours, wsJobStatusSkin, wsTourStatusSkin } from '../../../lib/well-servicing';
import type { WsFieldJob, WsFieldTourSummary } from '../../../types/well-servicing';
import { PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from '../../pressure-testing/field/PtFieldBits';
import { WS_SOUR_SKIN } from '../WsBits';

/** Where today's sheet for this job opens: the sheet itself, or the page that opens one. */
export const todaySheetPath = (job: Pick<WsFieldJob, 'id' | 'today_tour_id'>, today: string | null): string | null =>
  job.today_tour_id ? `/field/ws/tours/${job.today_tour_id}` : today ? `/field/ws/jobs/${job.id}/tours/${today}` : null;

function JobCard({ job, today }: { job: WsFieldJob; today: string | null }) {
  const to = todaySheetPath(job, today);
  const body = (
    <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
          <PtFieldBadge skin={wsJobStatusSkin(job.status, job.status_label)} />
          {job.well_sour && <PtFieldBadge skin={WS_SOUR_SKIN} />}
        </div>
        <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
        <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
          <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
          {job.well_name || '—'}
          {job.well_uwi || job.well_api_number ? ` · ${job.well_uwi || job.well_api_number}` : ''}
        </p>
        <p className="mt-1 flex items-center gap-1 text-xs text-fieldiq-muted">
          {job.today_tour_id ? (
            <>
              <CheckCircle2 className="size-3.5 text-fi-green" aria-hidden="true" />
              Today&apos;s sheet is open · {job.rig_number || '—'}
            </>
          ) : job.rig_id ? (
            `No sheet yet today · ${job.rig_number || '—'}`
          ) : (
            'This job has no rig yet. Ask the office.'
          )}
        </p>
      </div>
      {to && job.rig_id && <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />}
    </PtFieldCard>
  );
  return to && job.rig_id ? (
    <Link to={to} className="block" data-testid="ws-my-job" aria-label={`${job.job_number} today's tour sheet`}>
      {body}
    </Link>
  ) : (
    <div data-testid="ws-my-job">{body}</div>
  );
}

function TourRow({ tour }: { tour: WsFieldTourSummary }) {
  return (
    <Link to={`/field/ws/tours/${tour.id}`} className="block" data-testid="ws-my-tour">
      <PtFieldCard className="flex items-center gap-3 px-4 py-3 active:bg-fieldiq-dark/50">
        <ClipboardList className="size-5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-extrabold text-white">
            {wsDay(tour.work_date)} · {tour.job_number || '—'}
          </p>
          <p className="mt-0.5 text-xs text-fieldiq-text">
            {tour.start_time && tour.end_time ? `${tour.start_time}–${tour.end_time}` : '—'} · {wsHours(tour.activity_hours)} of {wsHours(tour.tour_hours)}
            {tour.complete ? '' : ' · gaps'}
          </p>
        </div>
        <PtFieldBadge skin={wsTourStatusSkin(tour.status, tour.status_label)} />
      </PtFieldCard>
    </Link>
  );
}

export default function WsMyToursScreen() {
  const query = useWsFieldMyTours();
  const jobs = query.data?.data.jobs ?? [];
  const tours = query.data?.data.tours ?? [];
  const today = query.data?.meta.today ?? null;

  return (
    <PtFieldPage title="My Tours" backTo="/field" testId="ws-my-tours">
      <PtFieldQueryState query={query} subject="your tour sheets">
        <section aria-labelledby="ws-group-jobs">
          <PtFieldSectionTitle>
            <span id="ws-group-jobs">Today{today ? ` · ${wsDay(today)}` : ''}</span>
          </PtFieldSectionTitle>
          {jobs.length === 0 ? (
            <PtFieldEmpty>You are not on any open well servicing job. The office puts you on a rig&apos;s crew.</PtFieldEmpty>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <JobCard key={j.id} job={j} today={today} />
              ))}
            </div>
          )}
        </section>
        <section aria-labelledby="ws-group-recent">
          <PtFieldSectionTitle>
            <span id="ws-group-recent">Last 14 days · {tours.length}</span>
          </PtFieldSectionTitle>
          {tours.length === 0 ? (
            <PtFieldEmpty>No tour sheet in the last 14 days.</PtFieldEmpty>
          ) : (
            <div className="space-y-2">
              {tours.map((t) => (
                <TourRow key={t.id} tour={t} />
              ))}
            </div>
          )}
        </section>
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
